import {
  useEffect,
  Dispatch,
  useState,
  SetStateAction,
  useCallback,
} from 'react';
import { IError, IUser, ResponseCSOSHeaders, useUserContext } from '../common';
import { getLegacyCerts, ICerts } from '.';
import { pathOr } from 'rambda';
import { noop } from 'lodash/fp';
import { left, right, fold } from 'fp-ts/lib/Either';
import { flow } from 'fp-ts/lib/function';

export const useLegacyCertSearch = ({
  email,
  deaNumber,
  serialNumber,
  page,
  pageSize,
}: {
  email: string | null;
  deaNumber: string | null;
  serialNumber: string | null;
  page: number;
  pageSize: number;
}) => {
  const { user } = useUserContext();

  const [certs, setcerts] = useState<ICerts>({ total: 0, content: [] });
  const [loading, setloading] = useState<boolean | null | undefined>();

  useEffect(() => {
    flow(
      startRequest,
      fold<
        undefined,
        {
          hR: ResponseCSOSHeaders;
          email: string | null;
          deaNumber: string | null;
          serialNumber: string | null;
          page: number;
          pageSize: number;
        },
        void
      >(
        loading === false ? noop : () => setloading(void 0),
        flow(handleRequest(setcerts)(setloading))
      )
    )({
      user,
      email,
      deaNumber,
      serialNumber,
      loading,
      page,
      pageSize,
    });
  }, [user, email, deaNumber, serialNumber, page, pageSize, loading]);

  const load = useCallback(() => setloading(null), []);
  return {
    certs,
    loading,
    load,
  };
};

const handleRequest =
  (dispatch: Dispatch<SetStateAction<ICerts>>) =>
    (setloading: (value: SetStateAction<boolean | null | undefined>) => void) =>
      ({
        hR,
        email,
        deaNumber,
        serialNumber,
        page,
        pageSize,
      }: {
        hR: ResponseCSOSHeaders;
        email: string | null;
        deaNumber: string | null;
        serialNumber: string | null;
        page: number;
        pageSize: number;
      }) => {
        getLegacyCerts(hR)({
          email,
          deaNumber,
          serialNumber,
          page,
          size: pageSize,
        }).then(
          fold<IError, ICerts, void>(
            () => setloading(false),
            flow(dispatch, () => setloading(true))
          )
        );
      };

const startRequest = ({
  user,
  email,
  deaNumber,
  serialNumber,
  page,
  pageSize,
  loading,
}: {
  user: IUser;
  email: string | null;
  deaNumber: string | null;
  serialNumber: string | null;
  page: number;
  pageSize: number;
  loading: boolean | null | undefined;
}) =>
  pathOr(void 0, 'icam.headers')(user) !== void 0 &&
    loading === null &&
    (email !== null || deaNumber !== null || serialNumber !== null)
    ? right({
      deaNumber: deaNumber?.toUpperCase() ?? null,
      email: email?.toUpperCase() ?? null,
      serialNumber: serialNumber?.toUpperCase() ?? null,
      page,
      pageSize,
      hR: pathOr({}, 'icam.headers')(user) as ResponseCSOSHeaders,
    })
    : left(void 0);
